import { insightWorkbenchApi } from '../api/InsightWorkbenchApi'
import type { ExecutePayloadInput, ExecuteResponse } from '../models/execute.types'
import type { ExecuteInsightCommand, ExecuteInsightOutcome } from '../models/workbench.types'
import { buildPreviewRequestSignature, hasExecutionConfig } from '../service/insightWorkbench'

import { ApiError } from '@/shared/http'

interface ExecuteInsightApiPort {
  execute: (
    accountId: number,
    workspaceId: number,
    payload: ExecutePayloadInput,
    signal?: AbortSignal,
  ) => Promise<ExecuteResponse>
}

export class ExecuteInsightFlow {
  constructor(private readonly api: ExecuteInsightApiPort = insightWorkbenchApi) {}

  async execute(command: ExecuteInsightCommand): Promise<ExecuteInsightOutcome> {
    const { payload } = command
    if (!hasExecutionConfig(payload)) {
      return { kind: 'invalid', errorKey: 'insights.sysadmin.execute.configRequired' }
    }

    const signature = buildPreviewRequestSignature(payload)
    const isDryRun = payload.dry_run !== false
    if (!isDryRun && command.previewSignature !== signature) {
      return { kind: 'blocked', errorKey: 'insights.sysadmin.execute.previewFirst' }
    }

    try {
      const response = await this.api.execute(
        command.accountId,
        command.workspaceId,
        payload,
        command.signal,
      )
      switch (response.status) {
        case 'preview_ready':
          return {
            kind: 'preview_ready',
            insight: response.insight ?? null,
            requestSignature: signature,
          }
        case 'skipped':
          return {
            kind: 'skipped',
            message: response.message?.trim() || 'insights.sysadmin.preview.skippedFallback',
          }
        case 'created':
          return { kind: 'created', insight: response.insight ?? null }
        default:
          return {
            kind: 'error',
            error: ApiError.fromAxios(new Error('Unexpected insight execute status')),
          }
      }
    } catch (cause) {
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      return error.kind === 'canceled' ? { kind: 'canceled' } : { kind: 'error', error }
    }
  }
}
